import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import MedicalInformationOutlinedIcon from '@mui/icons-material/MedicalInformationOutlined';
import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import { Formatter } from '@omnigenbiodata/ui';
import moment from 'moment';
import { OrderStatus, Participant } from '../../../../../../../../core/types/common.types';

interface ParticipantTimelineProps {
  participant: Participant;
}

type TimelineColour = 'success' | 'error' | 'inherit' | 'grey' | 'primary' | 'secondary' | 'info' | 'warning';

interface ParticipantEvent {
  name: string;
  description?: string | null;
  date: string;
  color: TimelineColour;
  icon: ReactNode;
  key?: string;
}

interface PotentialParticipantEvent extends Omit<ParticipantEvent, 'date'> {
  date: string | null | undefined;
}

function hasDate(event: PotentialParticipantEvent): event is ParticipantEvent {
  return Boolean(event.date);
}

function formatDate(value: string) {
  return Formatter({ input: value, format: 'friendlyDate' });
}

function formatTime(value: string) {
  return moment(value).isValid() ? moment(value).format('HH:mm') : '';
}

export function getSampleStatusDescription(sampleStatus: OrderStatus): string {
  switch (sampleStatus) {
    case OrderStatus.PENDING:
      return 'Sample Kit Pending';

    case OrderStatus.HOLD:
      return 'Sample Kit On Hold';

    case OrderStatus.CONFIRMED:
      return 'Sample Kit Confirmed';

    case OrderStatus.CREATED:
      return 'Sample Kit Label Printed';

    case OrderStatus.DISPATCHED:
      return 'Sample Kit Dispatched';

    case OrderStatus.RECEIVED:
      return 'Sample Received';

    case OrderStatus.STORED:
      return 'Sample Stored';

    case OrderStatus.QCFAIL:
      return 'Sample Failed QC';

    case OrderStatus.QCSUCCESS:
      return 'Sample Passed QC';

    case OrderStatus.DESTROYED:
      return 'Sample Destroyed';

    case OrderStatus.PROCESSED:
      return 'Ancestry Loaded';

    default:
      return 'Unknown Sample Status';
  }
}

function getEventTone(color: TimelineColour) {
  if (color === 'success') {
    return {
      borderColor: 'success.light',
      bgcolor: 'success.lighter',
      chipColor: 'success' as const,
    };
  }

  if (color === 'error') {
    return {
      borderColor: 'error.light',
      bgcolor: 'error.lighter',
      chipColor: 'error' as const,
    };
  }

  if (color === 'warning') {
    return {
      borderColor: 'warning.light',
      bgcolor: 'warning.lighter',
      chipColor: 'warning' as const,
    };
  }

  return {
    borderColor: 'divider',
    bgcolor: 'background.paper',
    chipColor: 'primary' as const,
  };
}

function getParticipantEvents(participant: Participant): ParticipantEvent[] {
  const events: PotentialParticipantEvent[] = [
    {
      name: 'Enrolled',
      date: participant.enrolmentTs,
      color: 'success',
      icon: <HowToRegOutlinedIcon fontSize="small" />,
    },
  ];

  if (participant.withdrawalLevel) {
    events.push({
      name: 'Withdrawn',
      description: participant.withdrawalLevel,
      date: participant.withdrawalTs,
      color: 'error',
      icon: <RemoveCircleOutlineIcon fontSize="small" />,
    });
  }

  if (participant.ehr?.dataHasArrivedTs) {
    events.push({
      name: 'EHR Arrived',
      date: participant.ehr.dataHasArrivedTs,
      color: 'primary',
      icon: <MoveToInboxOutlinedIcon fontSize="small" />,
    });
  }

  if (participant.ehr?.ehrUpdates) {
    participant.ehr?.ehrUpdates.forEach((eu) => {
      if (eu?.ehrStatus === 'SUCCESS') {
        events.push({
          name: 'EHR Success',
          date: eu.ehrUpdatedTs,
          color: 'primary',
          icon: <MedicalInformationOutlinedIcon fontSize="small" />,
        });
      }

      if (eu?.ehrStatus === 'FAILED') {
        events.push({
          name: 'EHR Failed',
          date: eu.ehrUpdatedTs,
          color: 'error',
          icon: <RemoveCircleOutlineIcon fontSize="small" />,
        });
      }

      if (eu?.ehrStatus === 'DECEASED') {
        events.push({
          name: 'EHR Deceased Notification',
          date: eu.ehrUpdatedTs,
          color: 'error',
          icon: <RemoveCircleOutlineIcon fontSize="small" />,
        });
      }
    });
  }

  if (participant.sample?.sampleUpdates) {
    participant.sample?.sampleUpdates.forEach((su) => {
      events.push({
        name: getSampleStatusDescription(su.orderStatus),
        description: participant.sample.sampleBarcode,
        date: su.orderUpdatedTs,
        color: 'primary',
        icon: <BiotechOutlinedIcon fontSize="small" />,
      });
    });
  }

  if (participant.lab?.shipped) {
    events.push({
      name: 'Sample Shipped',
      description: participant.sample?.sampleBarcode,
      date: participant.lab.shippedDate,
      color: 'primary',
      icon: <BiotechOutlinedIcon fontSize="small" />,
    });
  }

  return events
    .filter(hasDate)
    .map((event) => {
      return { ...event, key: `${event.name}${moment(event.date).format('YYYYMMDD')}` };
    })
    .reduce<ParticipantEvent[]>((prev: ParticipantEvent[], cur: ParticipantEvent) => {
      const duplicate = prev.some((event) => event.key === cur.key);

      if (!duplicate) {
        prev.push(cur);
      }

      return prev;
    }, [])
    .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
}

function ParticipantTimeline({ participant }: ParticipantTimelineProps) {
  const events = getParticipantEvents(participant);

  return (
    <Box mb={2}>
      <Paper elevation={4}>
        <Box p={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              gap: 1,
              mb: 2,
            }}
          >
            <ScheduleOutlined color="primary" />

            <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', m: 0 }}>
              Timeline
            </Typography>
          </Box>

          {events.length > 0 ? (
            <Timeline
              position="right"
              sx={{
                my: 0,
                p: 0,
                [`& .MuiTimelineItem-root:before`]: {
                  flex: 0,
                  padding: 0,
                },
              }}
            >
              {events.map((timelineEvent, index) => {
                const tone = getEventTone(timelineEvent.color);

                return (
                  <TimelineItem key={`${timelineEvent.name}-${timelineEvent.date}`}>
                    <TimelineOppositeContent
                      sx={{
                        flex: 0.35,
                        pt: 1.25,
                        pr: 2,
                        minWidth: 82,
                      }}
                      align="right"
                    >
                      <Typography variant="body2" component="p" sx={{ fontWeight: 700 }}>
                        {formatDate(timelineEvent.date)}
                      </Typography>

                      {formatTime(timelineEvent.date) && (
                        <Typography variant="caption" component="p" color="text.secondary">
                          {formatTime(timelineEvent.date)}
                        </Typography>
                      )}
                    </TimelineOppositeContent>

                    <TimelineSeparator>
                      <TimelineDot
                        color={timelineEvent.color}
                        sx={{
                          my: 1,
                          boxShadow: 2,
                          '& .MuiSvgIcon-root': {
                            fontSize: 18,
                          },
                        }}
                      >
                        {timelineEvent.icon}
                      </TimelineDot>

                      {index < events.length - 1 && (
                        <TimelineConnector
                          sx={{
                            bgcolor: 'divider',
                            width: 2,
                          }}
                        />
                      )}
                    </TimelineSeparator>

                    <TimelineContent sx={{ py: 0.75, px: 2 }}>
                      <Box
                        sx={{
                          border: 1,
                          borderColor: tone.borderColor,
                          bgcolor: tone.bgcolor,
                          borderRadius: 2,
                          p: 1.5,
                          boxShadow: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: timelineEvent.description ? 0.5 : 0,
                          }}
                        >
                          <Typography variant="body1" fontWeight="bold" component="p">
                            {timelineEvent.name}
                          </Typography>
                          <Chip
                            label={timelineEvent.color === 'error' ? 'Alert' : 'Event'}
                            color={tone.chipColor}
                            size="small"
                            variant="outlined"
                          />
                        </Box>

                        {timelineEvent.description && (
                          <Typography variant="body2" component="p" color="text.secondary">
                            {timelineEvent.description}
                          </Typography>
                        )}
                      </Box>
                    </TimelineContent>
                  </TimelineItem>
                );
              })}
            </Timeline>
          ) : (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="body1" component="p" color="text.secondary">
                No timeline events recorded.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default ParticipantTimeline;
